import { useMemo, useState, useCallback, type Ref } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnDef,
} from '@tanstack/react-table';
import type {
  DynamicTableColumnConfig,
  DynamicTableProps,
  DynamicTableViewMode,
} from './dynamic-table-types';
import { DynamicTableHeader } from './DynamicTableHeader';
import { DynamicTableListView } from './DynamicTableListView';
import { DynamicTableCardView } from './DynamicTableCardView';
import { DynamicTableKanbanView } from './DynamicTableKanbanView';
import { DynamicTablePagination } from './DynamicTablePagination';
import { renderBadge, getDisplayLabel, type ColumnMeta } from './DynamicTableValueRenderers';

const TH_BASE = 'px-6 py-4 text-left font-semibold text-muted-foreground uppercase tracking-wider';
const TD_BASE = 'px-6 py-4 whitespace-nowrap';
const TH_COMPACT =
  'px-2 py-1.5 text-left font-semibold text-muted-foreground uppercase tracking-wider';
const TD_COMPACT = 'px-2 py-1.5 whitespace-nowrap';

function getFontSizeClasses(fontSize: 'xs' | 'sm' | 'base'): { th: string; td: string } {
  switch (fontSize) {
    case 'xs':
      return { th: 'text-xs', td: 'text-xs' };
    case 'base':
      return { th: 'text-sm', td: 'text-base' };
    default:
      return { th: 'text-xs', td: 'text-sm' };
  }
}

/** Default page size options when pagination is enabled. */
const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

function buildColumnDefs<T>(configs: DynamicTableColumnConfig<T>[]): ColumnDef<T, unknown>[] {
  return configs.map(col => {
    const accessorKey = (col.accessorKey ?? col.id) as keyof T | undefined;
    const meta = {
      ...(col.meta as ColumnMeta),
      widthMode: col.widthMode,
      widthSize: col.size,
      widthMinSize: col.minSize,
      widthMaxSize: col.maxSize,
      displayLabelOptions: col.displayLabelOptions,
    } as ColumnMeta & { displayLabelOptions?: ReadonlyArray<{ value: string; label: string }> };
    const def: ColumnDef<T, unknown> = {
      id: col.id,
      header: () => col.header,
      enableSorting: col.sortable ?? false,
      size: col.size,
      minSize: col.minSize,
      maxSize: col.maxSize,
      meta,
      ...(accessorKey ? { accessorKey } : {}),
    };
    if (col.cell) {
      def.cell = ({ row, getValue }) =>
        col.cell!({
          row: row.original,
          value: getValue(),
          index: row.index,
        });
    } else if (col.columnType === 'badge') {
      def.cell = ({ getValue }) => renderBadge(getValue(), meta);
    } else {
      // Default cell renderer that respects displayLabelOptions for any column type
      def.cell = ({ getValue }) => {
        const raw = getValue();
        const mapped = getDisplayLabel(raw, meta);
        if (mapped !== undefined) {
          return mapped;
        }
        if (raw == null || raw === '') {
          return '—';
        }
        if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
          return String(raw);
        }
        return '—';
      };
    }
    return def;
  });
}

export function DynamicTable<T extends object>(props: Readonly<DynamicTableProps<T>>) {
  const {
    features = {},
    columns: columnConfigs,
    rows: rowsConfig,
    controllers = {},
    isLoading = false,
    loadingMessage = 'Loading...',
    emptyMessage = 'No data',
    toolbar,
    toolbarActions,
    bulkActionsBar,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    className = '',
    viewMode: viewModeProp,
    onViewModeChange,
    kanbanConfig,
  } = props;

  const [viewModeInternal, setViewModeInternal] = useState<DynamicTableViewMode>('list');
  const viewMode = viewModeProp ?? viewModeInternal;
  const setViewMode = useCallback(
    (mode: DynamicTableViewMode) => {
      if (onViewModeChange) onViewModeChange(mode);
      else setViewModeInternal(mode);
    },
    [onViewModeChange]
  );

  const {
    columns: columnsFeature = true,
    sorting: sortingFeature = true,
    pagination: paginationFeature = true,
    globalFilter: globalFilterFeature = false,
    rowSelection: rowSelectionFeature = false,
    expandableRows: expandableFeature = false,
    stripedRows: stripedRowsFeature = true,
    fontSize: fontSizeFeature = 'xs',
    manualPagination = true,
    manualSorting = true,
    viewModeSwitch: viewModeSwitchFeature = false,
    compactPadding: compactPaddingFeature = true,
  } = features;

  const { th: thTextClass, td: tdTextClass } = getFontSizeClasses(fontSizeFeature);
  const thBase = compactPaddingFeature ? TH_COMPACT : TH_BASE;
  const tdBase = compactPaddingFeature ? TD_COMPACT : TD_BASE;
  const TH_CLASS = `${thBase} ${thTextClass}`;
  const TD_CLASS = `${tdBase} ${tdTextClass}`;

  const { data, getRowId, onRowClick, expandableContent, getRowRef } = rowsConfig;
  const sortController = controllers.sort;
  const paginationController = controllers.pagination;
  const selectionController = controllers.selection;
  const globalFilterController = controllers.globalFilter;

  const columnDefs = useMemo(() => buildColumnDefs<T>(columnConfigs), [columnConfigs]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const handleExpandedChange = useCallback((updaterOrValue: unknown) => {
    setExpanded(prev => {
      const next =
        typeof updaterOrValue === 'function'
          ? (updaterOrValue as (p: Record<string, boolean>) => Record<string, boolean>)(prev)
          : (updaterOrValue as Record<string, boolean>);
      return typeof next === 'boolean' ? {} : next;
    });
  }, []);

  const table = useReactTable({
    data,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: row => getRowId(row),
    manualPagination: paginationFeature && manualPagination,
    manualSorting: sortingFeature && manualSorting,
    pageCount: paginationController?.pageCount ?? -1,
    rowCount: paginationController?.rowCount ?? data.length,
    state: {
      sorting: sortController?.state,
      pagination: paginationController?.state,
      globalFilter: globalFilterController?.value,
      rowSelection: selectionController?.state,
      expanded: expandableFeature ? expanded : undefined,
    },
    onExpandedChange: expandableFeature
      ? (handleExpandedChange as (updater: unknown) => void)
      : undefined,
    onSortingChange: sortController?.onSortingChange,
    onPaginationChange: paginationController?.onPaginationChange,
    onGlobalFilterChange: globalFilterController?.onChange
      ? value => globalFilterController.onChange(String(value ?? ''))
      : undefined,
    onRowSelectionChange: selectionController?.onSelectionChange,
    enableRowSelection: rowSelectionFeature,
  });

  const safePage = paginationController
    ? Math.min(
        Math.max(1, paginationController.state.pageIndex + 1),
        Math.max(1, paginationController.pageCount)
      )
    : 1;
  const pageSize = paginationController?.state.pageSize ?? pageSizeOptions[0];
  const totalRecords = paginationController?.rowCount ?? data.length;
  const totalPages = Math.max(1, paginationController?.pageCount ?? 1);

  const colSpan = columnDefs.length + (rowSelectionFeature ? 1 : 0) + (expandableFeature ? 1 : 0);

  const handlePageSizeChange = useCallback(
    (nextPageSize: number) => {
      if (!paginationController) return;
      paginationController.onPaginationChange(prev => ({
        ...prev,
        pageSize: nextPageSize,
        pageIndex: 0,
      }));
    },
    [paginationController]
  );

  const handlePrevPage = useCallback(() => {
    if (!paginationController) return;
    paginationController.onPaginationChange(prev => ({
      ...prev,
      pageIndex: Math.max(0, prev.pageIndex - 1),
    }));
  }, [paginationController]);

  const handleNextPage = useCallback(() => {
    if (!paginationController) return;
    paginationController.onPaginationChange(prev => ({
      ...prev,
      pageIndex: Math.min(totalPages - 1, prev.pageIndex + 1),
    }));
  }, [paginationController, totalPages]);

  const handleGoToPage = useCallback(
    (page: number) => {
      if (!paginationController) return;
      paginationController.onPaginationChange(prev => ({
        ...prev,
        pageIndex: page - 1,
      }));
    },
    [paginationController]
  );

  return (
    <div className={`overflow-hidden ${className}`.trim()}>
      <DynamicTableHeader
        globalFilterEnabled={globalFilterFeature}
        globalFilterController={globalFilterController}
        toolbar={toolbar}
        toolbarActions={toolbarActions}
        viewModeSwitchEnabled={viewModeSwitchFeature}
        viewMode={viewMode}
        setViewMode={setViewMode}
        kanbanEnabled={!!kanbanConfig}
        paginationEnabled={paginationFeature}
        totalRecords={totalRecords}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={paginationFeature ? handlePageSizeChange : undefined}
      />

      {rowSelectionFeature && bulkActionsBar && (
        <div className="px-4 py-2 border-b border-border bg-muted/30 flex items-center gap-2 flex-wrap">
          {bulkActionsBar}
        </div>
      )}

      {viewMode === 'list' && (
        <DynamicTableListView<T>
          table={table}
          columnsFeature={columnsFeature}
          sortingFeature={sortingFeature}
          rowSelectionFeature={rowSelectionFeature}
          expandableFeature={expandableFeature}
          stripedRowsFeature={stripedRowsFeature}
          TH_CLASS={TH_CLASS}
          TD_CLASS={TD_CLASS}
          isLoading={isLoading}
          loadingMessage={loadingMessage}
          emptyMessage={emptyMessage}
          colSpan={colSpan}
          getRowRef={getRowRef as (row: T, index: number) => Ref<HTMLTableRowElement> | undefined}
          onRowClick={onRowClick}
          expandableContent={expandableContent}
        />
      )}

      {viewMode === 'card' && (
        <DynamicTableCardView
          table={table}
          columns={columnConfigs}
          isLoading={isLoading}
          loadingMessage={loadingMessage}
          emptyMessage={emptyMessage}
          onRowClick={onRowClick}
        />
      )}

      {viewMode === 'kanban' && kanbanConfig && (
        <DynamicTableKanbanView
          table={table}
          columns={columnConfigs}
          kanbanConfig={kanbanConfig}
          isLoading={isLoading}
          loadingMessage={loadingMessage}
          emptyMessage={emptyMessage}
          onRowClick={onRowClick}
        />
      )}

      {paginationFeature && !isLoading && paginationController && (
        <DynamicTablePagination
          safePage={safePage}
          pageSize={pageSize}
          totalPages={totalPages}
          totalRecords={totalRecords}
          onPrev={handlePrevPage}
          onNext={handleNextPage}
          onPageChange={handleGoToPage}
        />
      )}
    </div>
  );
}
